import _ from "lodash";

import {
  inline,
} from "CommonState/action-helpers";
import apifetch from "CommonCore/apifetch";
import {
  ENDPOINT_SETTINGS,
} from "App/constants";

const FIXED_COLUMNS_STORAGE_PREFIX = "ui.fixedColumns";

function getFixedColumnsStorageKey(name) {
  return `${ FIXED_COLUMNS_STORAGE_PREFIX }.${ name }`;
}

function readFixedColumnsFromStorage(name) {
  if (typeof localStorage === "undefined") {
    return null;
  }

  try {
    const raw = localStorage.getItem(getFixedColumnsStorageKey(name));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch(err) {
    return null;
  }
}

function writeFixedColumnsToStorage(name, config) {
  if (typeof localStorage === "undefined") {
    return;
  }

  try {
    localStorage.setItem(getFixedColumnsStorageKey(name), JSON.stringify(config || {}));
  } catch(err) {
    return null;
  }
}

function clearFixedColumnsStorage(name) {
  if (typeof localStorage === "undefined") {
    return;
  }

  try {
    localStorage.removeItem(getFixedColumnsStorageKey(name));
  } catch(err) {
    return null;
  }
}

/**
 * @module UI-Liste
 * @desc
 *
 * UI-Listen versorgen die Daten und Funktionalität für tabellarische Ansichten.
 * Sie kapseln die Suche, Sortierung, Speichern/Laden von Filter-Kriterien u.s.w
 * von Listen ein. UI-Listen selber haben keine eigene Darstellung. Stattdessen,
 * kümmern sich Komponenten wie EntityTable um die Darstellung.
 *
 * UI-Listen sind von drei großen Teile gebaut:
 * * die im Redux-Store gespeicherte UI-List-Konfiguration
 * * die die Konfiguration ändernde Redux-Handlungen
 * * das UI-List-Selector, das alles zusammenbringt
 *
 * Im Store werden Daten gespeichert, wie z. B. was der benutzer in den Filterfelder
 * eingetragen hat oder welche Spalte die Sortierungsreihefolge festlegt. Jede UI-Liste
 * hat einen Name und dessen Daten werden unter diesem Name im Store gespeichert. Bspw.,
 * wenn die Liste heißt nutzerList, so werden die UI-Liste-Daten unter ui.uiNutzerList
 * im Store gespeichert.
 *
 * Die Daten lassen sich durch die aus dieser Datei exportierte Handlungen ändern.
 * Alle zu speichernden Änderungen werden automatisch zum Server gesendet.
 *
 * Das UI-List-Selector liest die UI-Daten und die Eingabe-Liste und produziert
 * eine gefilterte, sortierte Liste mit zusätzlichen Daten über die Paginierung,
 * Spalte-Konfiguration, gespeicherte Suchen u.s.w.
 *
 * UI-Listen werden für APIs üblicherweise automatisch erzeugt. Die meiste Fälle, wo
 * man eine tabellarische Ansicht braucht, werden von Page Factories (am höchsten Niveau),
 * oder von APIs (@myApi.uiList Dekorator) gedeckt. Für Sonderfälle kann man auch direkt eine UI-Liste erzeugen und
 * nutzen, wie z.B "currentNutzerRechteList" bei der nutzer-api.
 *
 * Checkliste für neue UI-Listen:
 * * ein Schema festlegen (genau wie bei APIs); das Schema muss unbedingt ein 'id' Feld haben.
 * * einen Eingabe-Selector festlegen, der die Eingabe-Liste erzeugt
 * * den Selector im die Liste anzeigenden Komponent durch @store injektieren
 * * die für das Schema notwendige Injects sammeln und im Komponent injektieren
 * * die UI-List-Daten und Injects dem EntityTable übergeben
 * * optionell Render-Methoden weiter festlegen
 *
 * @example
 * import {createUiListSelector, derived} from 'CommonState/action-helpers'
 * import {injectsFromSchemaAndRenderMethods} from 'CommonCore/schema-util'
 *
 * const xxSchema = {
 *  id: 'xx_id',
 *  table: [
 *    {
 *      field: 'xx_kbez',
 *      sortable: true,
 *      defaultSorted: true,
 *      filter: { ... }
 *      // u.s.w.
 *    }
 *    // u.s.w.
 *  ]
 * }
 *
 * const xxSelector = derived('xxList', (state, props) => "daten aus state und props holen",
 *   (arg1, arg2, arg3) => "xx Liste erzeugen")
 *
 * const xxUiListSelector = createUiListSelector('xxList', xxSchema, xxSelector);
 * const xxSchemaInjects = injectsFromSchemaAndRenderMethods(xxSchema);
 * const getXxInjects = createInjectsSelector(xxSchemaInjects);
 *
 * \@store(...xxSchemaInjects, xxUiListSelector)
 * const XxListPage extends Component {
 *   render() {
 *     return (
 *       <div>
 *         <EntityTable
 *           list='xxList'
 *           data={this.props.uiXxList}
 *           injects={getXxInjects(this.props)}
 *         />
 *         <button onClick={this.goToPage0}>Zur ersten Seite springen</button>
 *       </div>
 *       )
 *   }
 *   goToPage0 = () => {
 *     this.props.setUiListPage('xxList', 0);
 *   }
 * }
 */


function updateUiListProperty(name, property, body) {
  const uiName = `ui${ _.upperFirst(name) }`;

  const keys = {
    [`ui.${ uiName }`]: "deep+check",
  };

  return inline(keys, state => {
    let listData = _.get(state.ui, uiName);
    if (!listData) {
      _.set(state.ui, uiName, listData = {});
    }
    body(listData);
  });
}

function getUiListProperty(state, name, property) {
  return _.get(state, `ui.ui${ _.upperFirst(name) }.${ property }`);
}

function updateUiListFilter(name, field, body) {
  return dispatch => {
    dispatch(updateUiListProperty(name, "filter", listData => {
      const filter = listData.filter || (listData.filter = []);
      const i = _.findIndex(filter, _.isString(field) ? f => f.field === field : field);
      body(filter, i);
    }));
    dispatch(setUiListPage(name, 0));
  };
}

export function clearUiListFilters(name) {
  return replaceUiListFilter(name, []);
}

export function replaceUiListFilter(name, filter) {
  return updateUiListProperty(name, "filter", listData => {
    listData.filter = filter;
  });
}

export function setUiListFilterIfEmpty(name, defaultFilter) {
  return (dispatch, getState) => {
    if (!getUiListProperty(getState(), name, "filter")) {
      dispatch(updateUiListProperty(name, "filter", listData => {
        listData.filter = _.cloneDeep(defaultFilter);
      }));
    }
  };
}

export function getUiListFilters(name) {
    // Aktuelle Filtereinstellungen einer Liste liefern
  return (dispatch, getState) => {
    return getUiListProperty(getState(), name, "filter");
  };
}

export function toggleUiListFilter(name, field, initialValue) {
  return updateUiListFilter(name, field, (filter, i) => {
    if (i === -1) {
      filter.push({ field, criteria: initialValue });
    } else {
      filter.splice(i, 1);
    }
  });
}

export function setUiListFilterCriteria(name, field, criteria) {
  return updateUiListFilter(name, field, (filter, i) => {
    if (i !== -1) {
      filter[i].criteria = criteria;
    } else {
      filter.push({ field, criteria });
    }
  });
}

export function removeUiListFilter(name, field) {
  return updateUiListFilter(name, field, (filter, i) => {
    if (i !== -1) {
      if (filter[i].sort) {
        delete filter[i].criteria;
      } else {
        filter.splice(i, 1);
      }
    }
  });
}

export function setUiListStringFilter(name, stringFilter) {
  return updateUiListFilter(name, _.isString, (filter, i) => {
    if (i === -1 && stringFilter) {
      filter.push(stringFilter);
    } else if (stringFilter) {
      filter[i] = stringFilter;
    } else {
      filter.splice(i, 1);
    }
  });
}

export function loadUiListFilter(listName, filterName, defaultSort) {
  return updateUiListProperty(listName, ["filter", "currentUserFilter"], listData => {
    listData.sort = [];
    listData.filter = [];

    if (listData.userFilters[filterName]) {
      listData.userFilters[filterName].forEach(f => {
        if (_.isString(f)) {
          listData.filter.push(f);
        }
        if (f.criteria) {
          listData.filter.push({ field: f.field, criteria: f.criteria });
        }
        if (f.sort) {
          listData.sort.push({ key: f.field, reverse: f.sort.reverse, pos: f.sort.pos });
        }
      });
      listData.sort = _.sortBy(listData.sort, s => s.pos);
    } else {
      listData.sort = defaultSort;
    }

    listData.currentUserFilter = filterName;
  });
}

function updateUiListUserFilter(listName, body) {
  return async function(dispatch) {
    let userFilters;
    dispatch(updateUiListProperty(listName, ["userFilters", "currentUserFilter"], listData => {
      userFilters = listData.userFilters || (listData.userFilters = {});
      body(listData, userFilters);
    }));
    await apifetch(`${ ENDPOINT_SETTINGS }/userFilters.${ listName }`, {
      method: "PUT",
      body: userFilters,
    });
  };
}

export function saveUiListFilter(listName, filterName, filter) {
  return updateUiListUserFilter(listName, (listData, userFilters) => {
    userFilters[filterName] = filter || [];
    addSortToUserfilter(listData.sort, userFilters, filterName);
    listData.currentUserFilter = filterName;
  });
}

function addSortToUserfilter(sort, userFilters, filterName) {
  userFilters[filterName].forEach(f => delete f.sort);
  sort.forEach((s, index) => {
    const zugehFilter = userFilters[filterName].find(f => f.field === s.key);
    if (zugehFilter) {
      zugehFilter.sort = { pos: index, reverse: s.reverse || false };
    } else {
      userFilters[filterName].push({ field: s.key, sort: { pos: index, reverse: s.reverse || false } });
    }
  });
}

export function deleteUiListFilter(listName, filterName) {
  return updateUiListUserFilter(listName, (listData, userFilters) => {
    delete userFilters[filterName];
    if (listData.currentUserFilter == filterName) {
      listData.currentUserFilter = null;
      listData.filter = [];
    }
  });
}

export function setUiListSortKey(name, key, cascade) {
  return updateUiListProperty(name, "sort", listData => {
    if (_.isArray(key)) {
      listData.sort = key;
      return;
    }

    const sortList = listData.sort || (listData.sort = []);
    if (cascade) {
      const existing = _.find(sortList, sort => sort.key === key);
      if (existing) {
        existing.reverse = !existing.reverse;
      } else {
        sortList.push({ key });
      }
    } else {
      if (sortList.length > 1) {
        sortList.length = 0;
      }
      const sort = sortList[0] || (sortList[0] = {});
      if (sort.key === key) {
        sort.reverse = !sort.reverse;
      } else {
        sort.reverse = false;
        sort.key = key;
      }
    }
  });
}

export function setUiListSortKeyIfEmpty(name, keyF) {
  return (dispatch, getState) => {
    if (!getUiListProperty(getState(), name, "sort")) {
      dispatch(setUiListSortKey(name, keyF()));
    }
  };
}

export function setUiListItemsPerPage(name, count) {
  return updateUiListProperty(name, ["page", "itemsPerPage"], listData => {
    const page = listData.page || 0;
    const oldItemsPerPage = listData.itemsPerPage || 10;
    const firstItemInPage = page * oldItemsPerPage;
    const pageContainingFirstItem = 0 | (firstItemInPage / count);

    listData.page = pageContainingFirstItem;
    listData.itemsPerPage = count;
  });
}

export function setUiListPage(name, page) {
  return updateUiListProperty(name, "page", listData => listData.page = page);
}

const Placeholder = Object.freeze({ isPlaceholder: true });
export function loadUiListSettings(name) {
  return (dispatch, getState) => {
    const uiName = `ui.ui${ _.upperFirst(name) }`;
    const fixedKey = `${ uiName }.fixedColumnConfig`;
    const existingFixed = _.get(getState(), fixedKey);
    if (_.isNil(existingFixed)) {
      const storedFixed = readFixedColumnsFromStorage(name);
      if (storedFixed) {
        dispatch(inline({ [uiName]: "shallow" }, state => _.set(state, fixedKey, storedFixed)));
      }
    }

    [
      { key: "spalten", property: "columnConfig" },
      { key: "userFilters", property: "userFilters" },
    ].forEach(({ key, property }) => {
      const propkey = `${ uiName }.${ property }`;
      const existingValue = _.get(getState(), propkey);
      if (!existingValue) {
        dispatch(inline({ [uiName]: "shallow" }, state => _.set(state, propkey, Placeholder)));
        apifetch(`${ ENDPOINT_SETTINGS }/${ key }.${ name }`)
          .then(value => {
            dispatch(inline({ [uiName]: "shallow" }, state => {
              const listData = _.get(state, uiName);
              if (listData[property] === Placeholder) {
                listData[property] = value || {};
              }
            }));
            return null;
          }).catch(() => {
            return;
          });
      }
    });
  };
}

export function setUiListColumnVisible(name, field, visible) {
  return updateUiListProperty(name, "columnConfig", listData => {
    const columnConfig = listData.columnConfig || (listData.columnConfig = {});
    columnConfig[field] = visible;

    apifetch(`${ ENDPOINT_SETTINGS }/spalten.${ name }`, {
      method: "PUT",
      body: columnConfig,
    });
  });
}

export function setUiListColumnVisibleDefault(name) {
  return updateUiListProperty(name, "columnConfig", listData => {
    listData.columnConfig = {};
    apifetch(`${ ENDPOINT_SETTINGS }/spalten.${ name }`, {
      method: "PUT",
      body: {},
    });
  });
}

export function setUiListColumnFixed(name, field, fixed) {
  return updateUiListProperty(name, "fixedColumnConfig", listData => {
    const fixedColumnConfig = listData.fixedColumnConfig || (listData.fixedColumnConfig = {});
    fixedColumnConfig[field] = fixed;
    writeFixedColumnsToStorage(name, fixedColumnConfig);
  });
}

export function setUiListColumnFixedDefault(name) {
  return updateUiListProperty(name, "fixedColumnConfig", listData => {
    listData.fixedColumnConfig = {};
    clearFixedColumnsStorage(name);
  });
}

export function setUiListSelectionEnabled(name, enabled) {
  return updateUiListProperty(name, "selection", listData => {
    listData.selection = enabled ? listData.selection || {} : undefined;
  });
}

export function toggleUiListItemSelection(name, key) {
  return updateUiListProperty(name, "selection", listData => {
    const sel = listData.selection || (listData.selection = {});
    if (_.isArray(key)) {
      const value = !sel[key[0]];
      for (const k of key) {
        sel[k] = value;
      }
    } else {
      sel[key] = !sel[key];
    }
  });
}
